import { css } from '@emotion/react';
import { t } from 'i18n';
import type { Cabin, LowestFare, PassengerPrice, Price } from 'shared';
import { PAX_TYPE, formatCurrency, formatPoints } from 'shared';

import { DeviceType, useMaxDeviceWidth } from '../../hooks/useDeviceType';
import { theme } from '../theme';

const styles = {
  cabinTypes: css`
    display: flex;
    flex-grow: 1;
  `,
  fareColumnCommon: css`
    flex-basis: 50%;
    flex-grow: 1;
    border-left: 1px solid ${theme.colors.gray80};
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding: 0 2px;
    > p {
      margin: 0;
    }
  `,

  fareColumn: (selected: boolean, rowIsSelected: boolean) => css`
    cursor: pointer;
    background-color: ${selected ? theme.colors.gray90 : theme.colors.white};

    :last-of-type {
      border-top-right-radius: ${theme.borders.radius.md};
      border-bottom-right-radius: ${!selected ? theme.borders.radius.md : 0};
    }

    &:hover {
      background: ${theme.colors.gray80};
      &:last-of-type {
        border-top-right-radius: 6px;
        border-bottom-right-radius: ${!rowIsSelected ? '6px' : 0};
      }
    }
  `,
  fareColumnUnavailable: css`
    font-size: ${theme.fontSize.xs};
    cursor: default;
    pointer-events: none;
    text-align: center;
    color: var(--g4-color);

    @media (min-width: ${theme.breakpoints.mobileL}) {
      font-size: ${theme.fontSize.sm};
    }
  `,
  fareName: css`
    text-transform: capitalize;
    display: flex;
    font-size: ${theme.fontSize.xs};
    text-align: center;

    @media (min-width: ${theme.breakpoints.mobileL}) {
      font-size: ${theme.fontSize.sm};
    }
  `,
  originalFarePoints: css`
    font-size: ${theme.fontSize.xs};
    text-decoration: line-through;
  `,
  farePrice: css`
    font-size: ${theme.fontSize.xs};
    font-weight: ${theme.fontWeight.black};
    text-align: center;

    @media (min-width: ${theme.breakpoints.mobileL}) {
      font-size: ${theme.fontSize.md};
    }
  `,
};

type Props = {
  flightId: number | string;
  cabins: Cabin[] | Cabin[];
  lowestFares: LowestFare[] | undefined;
  boundColumns: string[] | undefined;
  selected: boolean;
  selectedCabin: Cabin | Cabin | undefined;
  pointOfSale: string;
  onClick: (cabinSelectorId: string, shouldToggle: boolean) => void;
  revenuePrice: (price: PassengerPrice) => number;
  isSarRebooking?: boolean;
};

export function FlightRowLowestFares({
  flightId,
  cabins,
  lowestFares,
  boundColumns,
  selected,
  selectedCabin,
  pointOfSale,
  onClick,
  revenuePrice,
  isSarRebooking = false,
}: Props) {
  const cabinLowestFares = getCabinLowestFares(flightId, cabins, lowestFares, boundColumns, selectedCabin?.selectorId);

  return (
    <div css={styles.cabinTypes}>
      {cabinLowestFares.map(cabinLowestFare => (
        <FlightRowLowestFare
          key={cabinLowestFare.cabin}
          cabinLowestFare={cabinLowestFare}
          selected={selected && cabinLowestFare.cabin.toLowerCase() === selectedCabin?.cabin.toLocaleLowerCase()}
          rowIsSelected={selected}
          pointOfSale={pointOfSale}
          onClick={onClick}
          revenuePrice={revenuePrice}
          isSarRebooking={isSarRebooking}
        />
      ))}
    </div>
  );
}

type ItemProps = {
  cabinLowestFare: CabinLowestFare;
  selected: boolean;
  rowIsSelected: boolean;
  pointOfSale: string;
  onClick: Props['onClick'];
  revenuePrice: (price: PassengerPrice) => number;
  isSarRebooking?: boolean;
};

function FlightRowLowestFare({
  cabinLowestFare,
  rowIsSelected,
  selected,
  pointOfSale,
  onClick,
  revenuePrice,
  isSarRebooking = false,
}: ItemProps) {
  const { price, fare, cabin } = cabinLowestFare;
  const isMobile = useMaxDeviceWidth(DeviceType.MobileL);

  if (price === undefined || fare === undefined) {
    return <div css={[styles.fareColumnCommon, styles.fareColumnUnavailable]}>{t('common:notAvailable')}</div>;
  }

  return (
    <div
      css={[styles.fareColumnCommon, styles.fareColumn(selected, rowIsSelected)]}
      onClick={() => onClick(fare.selectorId, true)}
      tabIndex={1}
      onKeyDown={e => {
        if (e.key === 'Enter') {
          onClick(fare.selectorId, true);
        }
      }}
    >
      <p css={styles.fareName}>{cabin.toLowerCase()}</p>
      {price.type === 'points' ? (
        <>
          {isSarRebooking ? null : (
            <>
              {price.discountedPoints > 0 ? (
                <>
                  <p css={styles.originalFarePoints}>{formatPoints(price.points, pointOfSale)}</p>
                  <p css={styles.farePrice}>{formatPoints(price.discountedPoints, pointOfSale, !isMobile)}</p>
                </>
              ) : (
                <p css={styles.farePrice}>{formatPoints(price.points, pointOfSale)}</p>
              )}
            </>
          )}
        </>
      ) : (
        <p css={styles.farePrice}>{formatCurrency(revenuePrice(price), pointOfSale)}</p>
      )}
    </div>
  );
}

type CabinLowestFare = {
  cabin: string;
  fare: LowestFare | undefined;
  price: PassengerPrice | undefined;
};

function getCabinLowestFares(
  flightId: number | string,
  cabins: Cabin[] | Cabin[],
  lowestFares: LowestFare[] | undefined,
  boundColumns: string[] | undefined,
  selectedProductId: string | undefined
): CabinLowestFare[] {
  if (!boundColumns) {
    return [];
  }

  const cabinLowestFares = boundColumns.reduce((cabinLowestFares: CabinLowestFare[], boundColumn: string) => {
    const matchedLowFare = lowestFares?.find(lowFare => lowFare.cabin === boundColumn);

    const price = matchedLowFare && getActualPrice(matchedLowFare, cabins, flightId, selectedProductId);

    cabinLowestFares.push({
      cabin: boundColumn,
      fare: matchedLowFare,
      price,
    });

    return cabinLowestFares;
  }, []);

  return cabinLowestFares;
}

function getActualPrice(
  fare: LowestFare,
  cabins: Cabin[] | Cabin[],
  flightId: number | string,
  selectedProductId: string | undefined
): Price | PassengerPrice | undefined {
  if (selectedProductId && `${flightId}_${fare.productId}` !== selectedProductId) {
    const selectedCabin = cabins.find(cabin => cabin.selectorId === selectedProductId);
    if (selectedCabin?.cabin === fare.cabin) {
      const pricePerPax = selectedCabin.price.pricePerPassengerType?.find(
        perPax => perPax.type === PAX_TYPE.ADULT || perPax.type === PAX_TYPE.YOUTH
      );
      return pricePerPax?.price;
    }
  }
  return fare.priceV2;
}
